import { ARCADE_FPS } from './common.js';
import type { ArcadeBaseState, ArcadeInput, ArcadeSimulation } from './common.js';
import { chope } from './chope.js';
import { kopiCatch } from './kopiCatch.js';
import { kuehStack } from './kuehStack.js';
import { mrtDash } from './mrtDash.js';

export { ARCADE_FPS } from './common.js';
export type { ArcadeBaseState, ArcadeFx, ArcadeInput, ArcadeSimulation } from './common.js';
export * from './rng.js';
export * from './kopiCatch.js';
export * from './mrtDash.js';
export * from './kuehStack.js';
export * from './chope.js';

export const ARCADE_GAME_IDS = ['kopi_catch', 'mrt_dash', 'kueh_stack', 'chope'] as const;
export type ArcadeGameId = (typeof ARCADE_GAME_IDS)[number];

export interface ArcadeGameInfo {
  id: ArcadeGameId;
  label: string;
  tagline: string;
  howToPlay: readonly string[];
  /** Score per K$ of reward (before caps). */
  scorePerDollar: number;
  /** Playfield size in game pixels. */
  width: number;
  height: number;
}

export const ARCADE_GAMES: Readonly<Record<ArcadeGameId, ArcadeGameInfo>> = Object.freeze({
  kopi_catch: {
    id: 'kopi_catch',
    label: 'Kopi Catch',
    tagline: 'Catch the kopi, dodge the durians!',
    howToPlay: [
      'Move the tray with the arrow keys or A/D, or drag along the counter.',
      'Catch kopi, teh and kaya toast. Golden kopi is worth 50!',
      'Flies cost points. Durians cost a life (you have 3).',
      'Every 5 catches in a row raises your multiplier, up to x5.',
    ],
    scorePerDollar: 30,
    width: 160,
    height: 200,
  },
  mrt_dash: {
    id: 'mrt_dash',
    label: 'MRT Dash',
    tagline: 'Mind the gap. And the luggage.',
    howToPlay: [
      'Jump with Space, W or Up (or tap the top half of the screen).',
      'Duck with S or Down (or hold the bottom half) to slide under signboards.',
      'Grab EZ-link cards for bonus points. Three bumps and you miss your train.',
      'The longer you last, the faster it gets.',
    ],
    scorePerDollar: 25,
    width: 160,
    height: 120,
  },
  kueh_stack: {
    id: 'kueh_stack',
    label: 'Kueh Stack',
    tagline: 'The tallest kueh lapis in Singapore.',
    howToPlay: [
      'Press Space or tap to drop the sliding layer.',
      'Anything hanging over the edge gets sliced off.',
      'Land it perfectly to keep the full width, and string perfects for big bonuses.',
      'Miss the stack completely and the tower is done.',
    ],
    scorePerDollar: 15,
    width: 160,
    height: 200,
  },
  chope: {
    id: 'chope',
    label: 'Chope!',
    tagline: 'Lunch crowd! Chope a table before the aunties do.',
    howToPlay: [
      'Tap (or press keys 1-9) a free table to chope it with a tissue packet.',
      'Faster chopes score more, and streaks raise your multiplier.',
      "Don't tap an uncle's table: he scolds you and you lose points.",
      'Lunch rush lasts one minute.',
    ],
    scorePerDollar: 20,
    width: 160,
    height: 200,
  },
});

// Simulations are stored loosely typed; callers pick a concrete one when they need its state.
const SIMULATIONS: Readonly<Record<ArcadeGameId, ArcadeSimulation<ArcadeBaseState>>> = {
  kopi_catch: kopiCatch as unknown as ArcadeSimulation<ArcadeBaseState>,
  mrt_dash: mrtDash as unknown as ArcadeSimulation<ArcadeBaseState>,
  kueh_stack: kuehStack as unknown as ArcadeSimulation<ArcadeBaseState>,
  chope: chope as unknown as ArcadeSimulation<ArcadeBaseState>,
};

export function arcadeSimulation(game: ArcadeGameId): ArcadeSimulation<ArcadeBaseState> {
  return SIMULATIONS[game];
}

export function isArcadeGame(value: unknown): value is ArcadeGameId {
  return typeof value === 'string' && (ARCADE_GAME_IDS as readonly string[]).includes(value);
}

/** Largest input log accepted for one run. */
export const MAX_ARCADE_INPUTS = 6000;
/** Longest run any game allows, in frames. */
export const MAX_ARCADE_FRAMES = 90 * ARCADE_FPS;

export type ReplayResult =
  | { ok: true; score: number; frames: number }
  | { ok: false; error: 'too_many_inputs' | 'bad_input' | 'unsorted' | 'after_end' };

/**
 * Replays a recorded run from its seed. The log must be sorted by frame, use
 * only the game's codes, and end no later than the game does. Returns the
 * score the simulation actually produces.
 */
export function replayArcadeRun(game: ArcadeGameId, seed: number, inputs: readonly ArcadeInput[]): ReplayResult {
  const sim = SIMULATIONS[game];
  if (inputs.length > MAX_ARCADE_INPUTS) return { ok: false, error: 'too_many_inputs' };

  let last = 0;
  for (const [frame, code] of inputs) {
    if (!Number.isInteger(frame) || frame < 0 || frame >= sim.maxFrames || !sim.validCode(code)) {
      return { ok: false, error: 'bad_input' };
    }
    if (frame < last) return { ok: false, error: 'unsorted' };
    last = frame;
  }

  const state = sim.create(seed);
  let cursor = 0;
  const codes: number[] = [];
  while (!state.over && state.frame < sim.maxFrames) {
    codes.length = 0;
    while (cursor < inputs.length && inputs[cursor]![0] === state.frame) {
      codes.push(inputs[cursor]![1]);
      cursor += 1;
    }
    sim.step(state, codes);
  }
  // Inputs after the game ended can only come from a tampered log.
  if (cursor < inputs.length) return { ok: false, error: 'after_end' };
  return { ok: true, score: state.score, frames: state.frame };
}

/* -------------------------------------------------------------------------- */
/* Rewards                                                                    */
/* -------------------------------------------------------------------------- */

export const ARCADE_RUN_CAP = 60;
export const ARCADE_DAILY_CAP = 400;
export const ARCADE_GUEST_RATE = 0.5;
export const ARCADE_GUEST_DAILY_CAP = 100;
/** Runs shorter than this pay nothing (quitting straight away is not a strategy). */
export const ARCADE_MIN_REWARD_FRAMES = 10 * ARCADE_FPS;

/** K$ for a run, before the daily cap. */
export function arcadeReward(game: ArcadeGameId, score: number, guest: boolean): number {
  const base = Math.min(ARCADE_RUN_CAP, Math.floor(score / ARCADE_GAMES[game].scorePerDollar));
  return guest ? Math.floor(base * ARCADE_GUEST_RATE) : base;
}

/* -------------------------------------------------------------------------- */
/* Dragon Boat Race                                                           */
/* -------------------------------------------------------------------------- */

export const RACE_MIN_PLAYERS = 2;
export const RACE_MAX_PLAYERS = 4;
export const RACE_DISTANCE = 1000;
/** Drum tempo: 90 beats a minute. */
export const RACE_BEAT_MS = 667;
/** Lobby countdown once two paddlers are in; cut short when everyone is ready. */
export const RACE_LOBBY_MS = 20_000;
export const RACE_READY_MS = 3_000;
/** "3, 2, 1, go!" before the first beat. */
export const RACE_INTRO_MS = 3_000;
export const RACE_MAX_MS = 60_000;
/** Clients must not send paddle strokes faster than this (the server enforces it too). */
export const RACE_MIN_STROKE_MS = 520;
export const RACE_WIN_REWARD = 40;
export const RACE_WINS_PER_DAY = 5;
